import { Briefcase, CircleDollarSign, FileText, Target } from "lucide-react";
import { useTranslate } from "ra-core";
import type { ReactNode } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { PulseData } from "./usePulseData";
import { useHomeFormat } from "./useHomeFormat";

/** «aprox.» badge: the figure is an estimate; the tooltip says why. */
export const ApproxBadge = ({ reason }: { reason: string }) => {
  const translate = useTranslate();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              className="home-badge home-approx"
              data-tone="neutral"
              aria-label={`${translate("desk.home.pulse.approx")}: ${reason}`}
            />
          }
        >
          {translate("desk.home.pulse.approx")}
        </TooltipTrigger>
        <TooltipContent className="max-w-72 text-pretty">
          {reason}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const Kpi = ({
  icon: Icon,
  label,
  badge,
  value,
  hint,
  children,
}: {
  icon: typeof Briefcase;
  label: string;
  badge?: ReactNode;
  value: string;
  hint: ReactNode;
  children?: ReactNode;
}) => (
  <div className="home-kpi">
    <div className="home-kpi__head">
      <span className="home-kpi__label">
        <Icon aria-hidden className="size-4" strokeWidth={1.75} />
        {label}
      </span>
      {badge}
    </div>
    <div className="home-kpi__body">
      <div className="min-w-0">
        <p className="home-kpi__value">{value}</p>
        <p className="home-kpi__hint">{hint}</p>
      </div>
      {children}
    </div>
  </div>
);

/** Small recessive bars: the last six months, current month emphasized. */
const MiniBars = ({ values, label }: { values: number[]; label: string }) => {
  const max = Math.max(...values, 1);
  return (
    <div className="home-minibars" role="img" aria-label={label}>
      {values.map((value, index) => (
        <span
          key={index}
          data-current={index === values.length - 1 || undefined}
          style={{ height: `${Math.max(8, (value / max) * 100)}%` }}
        />
      ))}
    </div>
  );
};

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** View B KPIs, all in money: pipeline, won, invoiced, outstanding. */
export const PulseKpis = ({ data }: { data: PulseData }) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.pulse.kpi.${key}`, options);
  const now = new Date();
  const wonRatio =
    data.won.expected > 0
      ? Math.min(1, data.won.amount / data.won.expected)
      : 0;

  return (
    <div className="home-kpis" role="group" aria-label={t("label")}>
      <Kpi
        icon={Briefcase}
        label={t("pipeline")}
        value={format.money(data.pipeline.amount)}
        hint={t("pipeline_hint", { smart_count: data.pipeline.count })}
      />
      <Kpi
        icon={Target}
        label={t("won")}
        badge={<ApproxBadge reason={t("won_approx")} />}
        value={format.money(data.won.amount)}
        hint={
          data.won.expected > 0
            ? t("won_hint", { amount: format.money(data.won.expected) })
            : t("won_none")
        }
      >
        {data.won.expected > 0 ? (
          <div
            className="home-ring"
            role="img"
            aria-label={t("won_ratio", { percent: format.percent(wonRatio) })}
            style={{ "--ratio": wonRatio } as React.CSSProperties}
          >
            <span>{format.percent(wonRatio)}</span>
          </div>
        ) : null}
      </Kpi>
      {data.invoicing ? (
        <Kpi
          icon={FileText}
          label={t("invoiced")}
          badge={
            <span className="home-badge" data-tone="neutral">
              {t("net")}
            </span>
          }
          value={format.money(data.invoicing.month.amount)}
          hint={t("invoiced_hint", {
            month: capitalize(format.monthName(now)),
            smart_count: data.invoicing.month.count,
          })}
        >
          <MiniBars
            values={data.invoicing.revenue.map((row) => row.invoiced)}
            label={t("invoiced_trend")}
          />
        </Kpi>
      ) : null}
      {data.invoicing ? (
        <Kpi
          icon={CircleDollarSign}
          label={t("outstanding")}
          value={format.money(data.invoicing.collect.total)}
          hint={t("outstanding_hint", {
            smart_count: data.invoicing.outstandingCount,
          })}
        >
          {data.invoicing.collect.overdue > 0 ? (
            <span className="home-badge" data-tone="negative">
              {t("outstanding_overdue", {
                amount: format.money(data.invoicing.collect.overdue),
              })}
            </span>
          ) : null}
        </Kpi>
      ) : null}
    </div>
  );
};
